import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../stores/authStore';
import { useTheme } from '../stores/themeStore';
import {
  LayoutDashboard,
  FileText,
  FilePlus,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  FileCheck,
  Building2,
  ChevronDown,
  Sun,
  Moon,
  Sparkles,
  Users,
} from 'lucide-react';
import { useState } from 'react';

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Tổng quan', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Tài liệu', href: '/documents', icon: FileText },
    { name: 'Tạo mới', href: '/documents/create', icon: FilePlus },
    { name: 'Danh bạ người ký', href: '/contacts', icon: Users },
    { name: 'Mẫu tài liệu', href: '/templates', icon: FileCheck },
    { name: 'Tổ chức', href: '/organization', icon: Building2 },
    { name: 'Cài đặt', href: '/settings', icon: Settings },
  ];

  const userInitial = user?.full_name ? user.full_name.trim().charAt(0).toUpperCase() : 'U';

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0b0f19] text-slate-900 dark:text-slate-100 transition-colors duration-200">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden transition-opacity"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-[#0e1424] border-r border-slate-200 dark:border-white/10 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label="Sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Brand Header */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-400 to-indigo-600 flex items-center justify-center text-white shadow-md shadow-sky-500/20">
                <FileCheck className="w-5 h-5" />
              </div>
              <div>
                <span className="text-base font-semibold tracking-tight text-slate-900 dark:text-white">PDF Sign</span>
                <span className="block text-[10px] uppercase font-medium tracking-wider text-sky-600 dark:text-sky-400">Enterprise</span>
              </div>
            </div>
            <button
              className="lg:hidden p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin" aria-label="Main navigation">
            <div className="px-3 pb-2 text-[11px] font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Menu chính
            </div>
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-normal transition-all ${
                    isActive
                      ? 'bg-sky-50 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300 font-medium border border-sky-200/60 dark:border-sky-500/30 shadow-sm'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100/80 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-slate-200'
                  }`
                }
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                <span>{item.name}</span>
              </NavLink>
            ))}
          </nav>

          {/* User info card at bottom */}
          <div className="p-3 m-3 rounded-xl bg-slate-100/70 dark:bg-slate-900/60 border border-slate-200/60 dark:border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-sky-500/10 dark:bg-sky-500/20 border border-sky-500/20 flex items-center justify-center font-medium text-sky-600 dark:text-sky-400 text-xs">
                {userInitial}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-900 dark:text-slate-200 truncate">{user?.full_name || 'Admin'}</p>
                <span className="inline-block px-1.5 py-0.2 rounded text-[10px] font-normal bg-slate-200/80 dark:bg-slate-800 text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  {user?.role || 'Admin'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content wrapper */}
      <div className="lg:pl-64 flex flex-col min-h-screen">
        {/* Top Navbar (Executive Linear / Vercel style) */}
        <header className="sticky top-0 z-30 bg-white/80 dark:bg-[#0b0f19]/80 backdrop-blur-md border-b border-slate-200 dark:border-white/10 transition-colors">
          <div className="flex items-center justify-between h-14 px-4 sm:px-6 lg:px-8 w-full">
            {/* Left: Mobile trigger & status */}
            <div className="flex items-center gap-3">
              <button
                className="lg:hidden p-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
              </button>

              <div className="live-pulse-badge hidden sm:inline-flex">
                <span className="pulse-dot"></span>
                <span>Hệ thống trực tuyến</span>
              </div>
            </div>

            {/* Right side controls */}
            <div className="flex items-center gap-2.5">
              {/* Dark / Light Mode Toggle Button */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 border border-transparent hover:border-slate-200 dark:hover:border-white/10 transition-all"
                title={theme === 'dark' ? 'Chuyển sang giao diện Sáng' : 'Chuyển sang giao diện Tối'}
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? (
                  <Sun className="w-4 h-4 text-amber-400 transition-transform rotate-0 hover:rotate-45" />
                ) : (
                  <Moon className="w-4 h-4 text-slate-600 transition-transform -rotate-12 hover:rotate-0" />
                )}
              </button>

              {/* Notifications */}
              <div className="relative">
                <button
                  className="p-2 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 border border-transparent hover:border-slate-200 dark:hover:border-white/10 relative transition-all"
                  onClick={() => setNotificationsOpen(!notificationsOpen)}
                  aria-label="Notifications"
                >
                  <Bell className="w-4 h-4" />
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-rose-500 rounded-full" />
                </button>

                {notificationsOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setNotificationsOpen(false)} />
                    <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-[#111827] rounded-xl shadow-xl border border-slate-200 dark:border-white/10 py-2 z-50 animate-slide-up">
                      <div className="px-4 py-2 border-b border-slate-100 dark:border-white/10 flex items-center justify-between">
                        <h3 className="font-semibold text-sm text-slate-900 dark:text-white">Thông báo</h3>
                        <button className="text-xs text-sky-600 dark:text-sky-400 hover:underline">Đã đọc</button>
                      </div>
                      <div className="px-4 py-6 text-center text-slate-500 text-xs">
                        Không có thông báo mới
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="h-4 w-px bg-slate-200 dark:bg-white/10 mx-1 hidden sm:block" />

              {/* User Menu Dropdown */}
              <div className="relative">
                <button
                  className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800/80 border border-transparent hover:border-slate-200 dark:hover:border-white/10 transition-all"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  <div className="w-7 h-7 rounded-lg bg-sky-500 text-white font-bold flex items-center justify-center text-xs shadow-sm">
                    {userInitial}
                  </div>
                  <span className="hidden sm:block text-xs font-medium text-slate-800 dark:text-slate-200">
                    {user?.full_name || 'Admin'}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400 hidden sm:block" />
                </button>

                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-[#111827] rounded-xl shadow-xl border border-slate-200 dark:border-white/10 py-1.5 z-50 animate-slide-up">
                      <div className="px-4 py-2.5 border-b border-slate-100 dark:border-white/10">
                        <p className="text-xs font-medium text-slate-900 dark:text-white truncate">{user?.full_name}</p>
                        <p className="text-[11px] text-slate-400 truncate">{user?.email}</p>
                      </div>
                      <NavLink
                        to="/settings"
                        className="flex items-center gap-2.5 px-4 py-2 text-xs font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Settings className="w-3.5 h-3.5 text-slate-400" />
                        Cài đặt hệ thống
                      </NavLink>
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-2.5 w-full px-4 py-2 text-xs font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/20"
                      >
                        <LogOut className="w-3.5 h-3.5" />
                        Đăng xuất
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Body - Full Width */}
        <main className="flex-1 p-4 sm:p-6 lg:px-8 w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}